/**
 * Throwing a potje away, on this device and on the server.
 *
 * The server first, then the local copy — and if the server cannot be reached,
 * nothing happens at all.
 *
 * That order is the point. Deleting locally while the row survives on the server
 * would look like it worked and then undo itself: an unfinished potje comes
 * straight back under "op een ander toestel begonnen", and a finished one keeps
 * counting in the standings. Half a delete is worse than none, so this refuses
 * rather than pretends.
 *
 * A potje that was never synced is not a special case: the server answers 204 for
 * something it does not have, so the local copy goes either way.
 */

import { ApiError, api } from '@/lib/api';
import { useScorekeeper } from '@/store/scorekeeper';
import { useKlaverjasScore } from '@/store/klaverjasScore';

export type ScoreKind = 'boerenbridge' | 'klaverjas';

export class DeleteFailed extends Error {
  constructor(readonly offline: boolean) {
    super(
      offline
        ? 'Geen verbinding met de server, dus het potje staat er nog. Probeer het zo nog eens.'
        : 'Dit potje kon niet worden weggegooid.'
    );
    this.name = 'DeleteFailed';
  }
}

export async function removeGame(id: string, kind: ScoreKind): Promise<void> {
  try {
    await api<void>(`/api/games/${id}`, { method: 'DELETE' });
  } catch (err) {
    throw new DeleteFailed(err instanceof ApiError && err.offline);
  }

  if (kind === 'klaverjas') useKlaverjasScore.getState().deleteGame(id);
  else useScorekeeper.getState().deleteGame(id);
}
